/**
 * Lift2Fit — the platform's chat interface (`#/admin/chat`, migration 0035/0037).
 *
 * This is the ADMIN'S CHAT, not a portal page: it renders without the admin
 * shell (no Overview/Businesses tabs around it), sits in its own window like the
 * coach's and lifter's `#/chat`, and is shaped the same way — a header with the
 * way back home, the conversation list on the left, the thread in the middle,
 * and the account's context on the right. The faces are different because the
 * operator is the other side of the conversation: the "chatters" are the
 * accounts asking for help, and the context is what that account thinks of the
 * platform (their review), not a training programme.
 *
 * Replies are written as the platform, so they arrive on the customer's side
 * labelled "Help". Opening a thread marks it read for the admin; the
 * customer's own read state is only ever moved by their own chat screen.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, type Role, type SupportConversation, type SupportThreadSummary } from '@/lib/api';
import { cn, timeAgo } from '@/lib/format';
import { Button } from '@/components/ui';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AlertIcon, ArrowRightIcon } from '@/lib/icons';
import { EmptyState, Field, LiveMessage, LoadingPanel, StatusChip } from '../components';
import { useAsync } from '../hooks';
import { chatDayKey, chatDayLabel, ChatAvatar, ChatIcon } from '../chat';
import { roleLabel } from './shared';
import { useSession } from '@/lib/useSession';
import { HelpReviewSummary, StarRow } from '../crm/HelpFeedbackPanel';

type QueueFilter = 'all' | 'unread';

function timeOf(iso: string | null): string {
  if (!iso) return '';
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return '';
  return new Date(parsed).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** The queue carries role names as strings; the shared label map expects `Role`. */
function roleList(roles: string[]): string {
  if (roles.length === 0) return 'Lifter';
  return roles.map((role) => roleLabel(role as Role)).join(', ');
}

function planLabel(plan: string | null): string {
  if (!plan || plan === 'free') return 'No paid plan';
  return plan;
}

export function AdminChatScreen() {
  const session = useSession();
  const queue = useAsync(() => api.adminSupportThreads(), []);
  const threads: SupportThreadSummary[] = queue.data?.data ?? [];

  const [filter, setFilter] = useState<QueueFilter>('all');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [conversation, setConversation] = useState<SupportConversation | null>(null);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [noticeTone, setNoticeTone] = useState<'info' | 'success' | 'danger'>('info');
  const endRef = useRef<HTMLDivElement | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);

  const visible = threads.filter((thread) => {
    if (filter === 'unread' && thread.unread === 0) return false;
    if (query.trim() === '') return true;
    const needle = query.trim().toLowerCase();
    return (
      thread.displayName.toLowerCase().includes(needle) ||
      thread.email.toLowerCase().includes(needle) ||
      (thread.lastMessage ?? '').toLowerCase().includes(needle)
    );
  });

  // The selection survives a queue refresh as long as its thread still exists.
  const activeId =
    selectedId && visible.some((thread) => thread.threadId === selectedId)
      ? selectedId
      : (visible[0]?.threadId ?? null);
  const active = visible.find((thread) => thread.threadId === activeId) ?? null;
  const unread = queue.data?.unread ?? 0;

  useEffect(() => {
    if (!activeId) {
      setConversation(null);
      return;
    }
    let alive = true;
    setConversationLoading(true);
    setConversationError(null);
    void api.adminSupportThread(activeId).then((result) => {
      if (!alive) return;
      setConversationLoading(false);
      if (!result.data) {
        setConversation(null);
        setConversationError(result.problem?.detail ?? 'That conversation did not load.');
        return;
      }
      setConversation(result.data);
      // The read happened server-side; reflect it here so the queue badge drops
      // without a second round trip.
      queue.reload();
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const messages = conversation?.messages ?? [];

  useEffect(() => {
    // Scroll the MESSAGE LIST, not the window: `scrollIntoView` also moves the
    // page, which clipped the chat's own header on mount.
    const list = messagesRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages.length, activeId]);

  const takeOver = async () => {
    if (!activeId) return;
    setSending(true);
    const result = await api.adminTakeoverThread(activeId);
    setSending(false);
    if (!result.data) {
      setNoticeTone('danger');
      setNotice(result.problem?.detail ?? 'That chat could not be taken over.');
      return;
    }
    setConversation(result.data);
    setNoticeTone('success');
    setNotice('You own this chat now — the client is no longer waiting.');
    queue.reload();
  };

  const reply = async (event: FormEvent) => {
    event.preventDefault();
    const body = draft.trim();
    if (body === '' || !activeId) return;
    setSending(true);
    setNotice(null);
    const result = await api.adminSupportReply(activeId, body);
    setSending(false);
    if (!result.data) {
      setNoticeTone('danger');
      setNotice(result.problem?.detail ?? 'That reply did not send. Try again.');
      return;
    }
    setDraft('');
    setNoticeTone('success');
    setNotice(`Replied to ${active?.displayName ?? 'the account'}.`);
    const refreshed = await api.adminSupportThread(activeId);
    if (refreshed.data) setConversation(refreshed.data);
    queue.reload();
  };

  // 0037 — the platform's own reviews, averaged over the rows that have one.
  const rated = threads.filter((thread) => thread.rating !== null);
  const ratedCount = rated.length;
  const ratedAverage =
    ratedCount === 0
      ? null
      : rated.reduce((sum, thread) => sum + (thread.rating ?? 0), 0) / ratedCount;

  return (
    <div className="l2f-chat-backdrop flex h-screen min-h-0 flex-col" data-testid="admin-chat">
      {/* The chat's own slim header — no portal tabs, no admin nav. */}
      <header
        className="flex flex-wrap items-center justify-between gap-3 px-4 pb-3 pt-5 sm:px-6 sm:pt-6"
        data-testid="admin-chat-header"
      >
        <div className="flex min-w-0 items-center gap-3">
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-chat-lg border border-hairline bg-surface text-accent shadow-float">
            <ChatIcon className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-muted">Platform admin</p>
            <h1 className="truncate font-display text-[22px] font-black uppercase leading-none tracking-[-0.01em] text-ink">
              Help
            </h1>
          </div>
          {unread > 0 ? (
            <span data-testid="admin-chat-unread">
              <StatusChip tone="accent" icon={<ChatIcon className="h-3.5 w-3.5" />}>
                {unread} unread
              </StatusChip>
            </span>
          ) : (
            <StatusChip tone="neutral">All read</StatusChip>
          )}
        </div>
        <div className="flex items-center gap-2">
          {ratedAverage !== null ? (
            <span className="hidden items-center gap-1.5 sm:flex" data-testid="admin-chat-average-rating">
              <StarRow rating={Math.round(ratedAverage)} />
              <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted">
                {ratedAverage.toFixed(1)}/5 · {ratedCount}
              </span>
            </span>
          ) : null}
          <ThemeToggle />
          <a
            href="#/admin"
            data-testid="admin-chat-back"
            className="inline-flex h-10 items-center gap-1.5 rounded-pill border border-hairline bg-surface px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-ink shadow-float transition-colors duration-150 hover:border-accent hover:text-accent"
          >
            Back to portal
            <ArrowRightIcon />
          </a>
        </div>
      </header>

      {queue.loading && !queue.data ? (
        <div className="p-4">
          <LoadingPanel label="Reading the help queue" rows={3} />
        </div>
      ) : queue.error && !queue.data ? (
        <div className="p-4">
          <EmptyState
            title="The help queue did not load"
            body={queue.error}
            icon={<AlertIcon className="h-8 w-8 text-accent" />}
          />
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 gap-3 p-3 pt-0 sm:gap-4 sm:p-6 sm:pt-0 lg:grid-cols-[330px_minmax(0,1fr)] xl:grid-cols-[330px_minmax(0,1fr)_320px]">
          {/* --- the queue: who is waiting on the platform -------------------- */}
          <aside
            className="flex min-h-[320px] flex-col overflow-hidden rounded-chat-lg border border-hairline bg-surface shadow-float lg:min-h-0"
            data-testid="admin-support-queue"
          >
            <div className="flex flex-col gap-2 border-b border-hairline px-3 py-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink">
                  Waiting
                  <span className="ml-1.5 text-ink-muted">{threads.length}</span>
                </span>
                <div className="flex gap-1">
                  {(['all', 'unread'] as const).map((option: QueueFilter) => (
                    <button
                      key={option}
                      type="button"
                      data-testid={`admin-support-filter-${option}`}
                      aria-pressed={filter === option}
                      onClick={() => setFilter(option)}
                      className={cn(
                        'h-8 border px-2 text-[10px] font-bold uppercase tracking-[0.06em]',
                        filter === option
                          ? 'border-ink bg-ink text-ink-inverse'
                          : 'border-hairline bg-surface text-ink hover:border-accent',
                      )}
                    >
                      {option === 'all' ? 'All' : 'Unread'}
                    </button>
                  ))}
                </div>
              </div>
              <input
                data-testid="admin-support-search"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search name, email or message"
                className="h-9 w-full rounded-pill border border-hairline bg-page-subtle px-3.5 text-[13px] text-ink outline-none focus:border-accent"
              />
            </div>

            <div className="min-h-0 max-h-[42vh] flex-1 overflow-y-auto p-2 lg:max-h-none">
              {visible.length === 0 ? (
                <p className="p-2 text-[13px] text-ink-muted" data-testid="admin-support-empty">
                  {threads.length === 0
                    ? 'Nobody has written to Help yet. Threads appear here the first time an account opens the conversation.'
                    : 'No thread matches this filter.'}
                </p>
              ) : (
                <ul className="flex flex-col gap-1" data-testid="admin-support-threads">
                  {visible.map((thread) => (
                    <li key={thread.threadId}>
                      <button
                        type="button"
                        data-testid="admin-support-thread"
                        data-thread-id={thread.threadId}
                        data-unread={thread.unread > 0 ? 'true' : 'false'}
                        aria-current={thread.threadId === activeId}
                        onClick={() => {
                          setSelectedId(thread.threadId);
                          setNotice(null);
                        }}
                        className={cn(
                          'flex w-full items-start gap-2.5 rounded-chat border px-3 py-2.5 text-left transition-all duration-150',
                          thread.threadId === activeId
                            ? 'border-accent bg-page-subtle shadow-float'
                            : 'border-hairline bg-surface hover:-translate-y-0.5 hover:border-accent hover:shadow-float',
                        )}
                      >
                        <ChatAvatar name={thread.displayName} />
                        <span className="flex min-w-0 flex-1 flex-col gap-1">
                          <span className="flex items-baseline justify-between gap-2">
                            <span className="truncate text-[13px] font-bold text-ink">{thread.displayName}</span>
                            {thread.unread > 0 ? (
                              <span className="shrink-0 rounded-pill bg-accent px-1.5 text-[10px] font-bold text-on-accent">
                                {thread.unread}
                              </span>
                            ) : null}
                          </span>
                          <span className="truncate text-[11px] text-ink-muted">{thread.email}</span>
                          <span className="line-clamp-2 text-[12px] text-ink-muted">
                            {thread.lastFromAdmin ? 'You: ' : ''}
                            {thread.lastMessage ?? 'No messages yet.'}
                          </span>
                          <span className="flex items-center justify-between gap-2 text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">
                            <span className="flex min-w-0 items-center gap-1.5">
                              <span className="truncate">
                                {roleList(thread.roles)} · {timeAgo(thread.lastMessageAt ?? thread.createdAt)}
                              </span>
                              {thread.sessionStatus === 'queued' ? (
                                <span className="shrink-0 rounded-pill border border-warning px-1.5 text-[9px] text-warning">
                                  Waiting
                                </span>
                              ) : thread.sessionStatus === 'bot' ? (
                                <span className="shrink-0 rounded-pill border border-accent px-1.5 text-[9px] text-accent">
                                  Assistant
                                </span>
                              ) : thread.sessionStaffName ? (
                                <span className="shrink-0 truncate rounded-pill border border-hairline px-1.5 text-[9px]">
                                  {thread.sessionStaffName}
                                </span>
                              ) : null}
                            </span>
                            {thread.rating !== null ? (
                              <span
                                data-testid="admin-support-thread-rating"
                                title={`Rated Lift2Fit ${thread.rating}/5`}
                              >
                                <StarRow rating={thread.rating} />
                              </span>
                            ) : null}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>

          {/* --- the thread -------------------------------------------------- */}
          <main
            className="flex min-h-[520px] min-w-0 flex-col overflow-hidden rounded-chat-lg border border-hairline bg-surface shadow-float lg:min-h-0"
            data-testid="admin-support-conversation"
          >
            {!activeId ? (
              <div className="flex flex-1 items-center justify-center p-6">
                <EmptyState
                  title="Pick a thread"
                  body="Choose an account on the left to read the conversation and answer as Help."
                  icon={<ChatIcon className="h-8 w-8 text-accent" />}
                />
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline bg-page-subtle px-4 py-3">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <ChatAvatar name={active?.displayName ?? 'Account'} size="md" />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-[15px] font-bold text-ink">
                        {active?.displayName ?? 'Account'}
                      </span>
                      <span className="truncate text-[11.5px] text-ink-muted">
                        {active?.email ?? ''} · {active ? roleList(active.roles) : ''} ·{' '}
                        {active ? planLabel(active.coachPlan) : ''}
                      </span>
                    </div>
                  </div>
                  <span className="flex flex-wrap items-center gap-1.5">
                    {conversation?.session ? (
                      <StatusChip
                        tone={
                          conversation.session.status === 'queued'
                            ? 'warning'
                            : conversation.session.status === 'bot'
                              ? 'accent'
                              : 'success'
                        }
                      >
                        {conversation.session.status === 'queued'
                          ? `Waiting${conversation.session.queuePosition ? ` · #${conversation.session.queuePosition}` : ''}`
                          : conversation.session.status === 'bot'
                            ? 'Assistant'
                            : `With ${conversation.session.staffName ?? 'a teammate'}`}
                      </StatusChip>
                    ) : (
                      <StatusChip tone="neutral">No open session</StatusChip>
                    )}
                    {active ? (
                      <StatusChip tone={active.status === 'open' ? 'accent' : 'neutral'}>
                        {active.status === 'open' ? 'Open' : 'Closed'}
                      </StatusChip>
                    ) : null}
                    {conversation?.session &&
                    (conversation.session.status === 'queued' ||
                      conversation.session.staffId !== session.user?.id) ? (
                      <button
                        type="button"
                        data-testid="admin-chat-takeover"
                        disabled={sending}
                        onClick={() => void takeOver()}
                        className="h-7 rounded-pill border border-accent bg-surface px-3 text-[10px] font-bold uppercase tracking-[0.08em] text-accent hover:bg-page-subtle"
                      >
                        Take over
                      </button>
                    ) : null}
                  </span>
                </div>

                {conversationLoading && !conversation ? (
                  <div className="p-4">
                    <LoadingPanel label="Opening the conversation" rows={2} />
                  </div>
                ) : conversationError ? (
                  <p className="p-4 text-[13px] text-danger" data-testid="admin-support-error">
                    {conversationError}
                  </p>
                ) : (
                  <>
                    <div
                      ref={messagesRef}
                      className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-4"
                      data-testid="admin-support-messages"
                    >
                      {messages.length === 0 ? (
                        <p className="text-[13px] text-ink-muted">
                          The thread exists but has no messages — the account opened help without writing.
                        </p>
                      ) : (
                        messages.map((message, index) => {
                          const previous = messages[index - 1];
                          const firstInRun = !previous || previous.fromAdmin !== message.fromAdmin;
                          const newDay =
                            !previous || chatDayKey(previous.createdAt) !== chatDayKey(message.createdAt);
                          return (
                            <div key={message.id} data-testid={`admin-support-message-${message.id}`}>
                              {newDay ? (
                                <div className="mb-2 mt-1 flex items-center gap-3">
                                  <span className="h-px flex-1 bg-hairline" />
                                  <span className="rounded-pill border border-hairline bg-surface px-3 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                                    {chatDayLabel(message.createdAt)}
                                  </span>
                                  <span className="h-px flex-1 bg-hairline" />
                                </div>
                              ) : null}
                            <div
                              className={cn(
                                'flex items-end gap-2',
                                message.fromAdmin ? 'justify-end' : 'justify-start',
                                firstInRun ? 'mt-2' : 'mt-0.5',
                              )}
                            >
                              {!message.fromAdmin ? (
                                <span className="w-7 shrink-0">
                                  <ChatAvatar name={active?.displayName ?? 'Account'} size="sm" />
                                </span>
                              ) : null}
                              <div
                                className={cn(
                                  'flex max-w-[78%] flex-col gap-1',
                                  message.fromAdmin ? 'items-end' : 'items-start',
                                )}
                              >
                                {firstInRun ? (
                                  <span className="px-1 text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">
                                    {message.fromAdmin ? 'Help' : active?.displayName ?? 'Account'}
                                  </span>
                                ) : null}
                                <span
                                  className={cn(
                                    'flex flex-col gap-1 whitespace-pre-wrap px-3.5 py-2.5 text-[13.5px] leading-relaxed text-ink',
                                    message.fromAdmin
                                      ? 'rounded-bubble rounded-br-chat-tail bg-bubble-mine'
                                      : 'rounded-bubble rounded-bl-chat-tail bg-page-subtle',
                                  )}
                                >
                                  <span>{message.body}</span>
                                  <span
                                    className={cn(
                                      'l2f-tabular font-mono text-[9.5px] leading-none text-ink-muted',
                                      message.fromAdmin ? 'self-end' : 'self-start',
                                    )}
                                  >
                                    {timeOf(message.createdAt)}
                                  </span>
                                </span>
                              </div>
                            </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={endRef} />
                    </div>

                    <form
                      onSubmit={reply}
                      className="m-3 flex flex-col gap-2 rounded-chat-lg border border-hairline bg-surface p-2 shadow-float"
                      data-testid="admin-support-form"
                    >
                      <Field label="Reply as Help">
                        <textarea
                          data-testid="admin-support-input"
                          value={draft}
                          onChange={(event) => setDraft(event.target.value)}
                          rows={2}
                          maxLength={4000}
                          placeholder="Answer in plain language — this arrives in the account’s own chat."
                          className="l2f-chat-well w-full resize-none rounded-chat px-3.5 py-2.5 text-[14px] text-ink outline-none placeholder:text-ink-muted focus:ring-1 focus:ring-accent"
                        />
                      </Field>
                      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                        <LiveMessage message={notice} tone={noticeTone} />
                        <Button
                          type="submit"
                          size="sm"
                          className="rounded-pill"
                          data-testid="admin-support-send"
                          disabled={sending || draft.trim() === ''}
                        >
                          {sending ? 'Sending…' : 'Send reply'}
                        </Button>
                      </div>
                    </form>
                  </>
                )}
              </>
            )}
          </main>

          {/* --- the account's context, not a training programme -------------- */}
          <aside
            className="hidden min-h-0 flex-col gap-3 overflow-y-auto rounded-chat-lg border border-hairline bg-surface p-3 shadow-float xl:flex"
            data-testid="admin-chat-rail"
          >
            <HelpReviewSummary
              review={conversation?.review ?? null}
              displayName={active?.displayName ?? 'This account'}
            />
            <section className="flex flex-col gap-1 rounded-chat border border-hairline bg-page-subtle p-3">
              <h2 className="text-[13px] font-bold uppercase tracking-[0.08em] text-ink">The queue</h2>
              <p className="text-[12px] leading-relaxed text-ink-muted">
                Every account owns exactly one thread, created the first time they open Help. Replies
                here land in their own chat; the platform side marks a thread read when it is opened.
              </p>
              <p className="text-[12px] text-ink-muted">
                <span className="font-bold text-ink">{threads.length}</span> thread(s) ·{' '}
                <span className="font-bold text-ink">{unread}</span> unread ·{' '}
                <span className="font-bold text-ink">{ratedCount}</span> rated
              </p>
            </section>
          </aside>
        </div>
      )}
    </div>
  );
}
